<template lang="html">
  <div id="tickets-page">
    <loading-component-s2 v-if="isLoading"></loading-component-s2>

    <section id="soporte" class="py-5 bg-light">
      <div class="container">
        <div class="text-center mb-5">
          <h2 class="h2 font-weight-bold text-dark mb-3">Centro de Soporte</h2>
          <p class="lead text-muted">
            Estamos aquí para ayudarte. Crea un ticket o revisa el estado de tus solicitudes.
          </p>
        </div>

        <div class="row">
          <!-- Crear Nuevo Ticket -->
          <div class="col-12 col-lg-6 mb-4 mb-lg-0">
            <div class="card border-0 shadow h-100">
              <div class="card-body p-4">
                <div class="d-flex align-items-center justify-content-between mb-4" v-if="!hideTicketForm">
                  <div class="d-flex align-items-center">
                    <div class="support-icon-plus rounded p-3 mr-3">
                      <i class="fa-solid fa-plus fa-lg text-danger"></i>
                    </div>
                    <h3 class="h4 font-weight-bold text-dark mb-0">Nuevo Ticket</h3>
                  </div>
                  <b-button
                    v-if="!showNewTicketForm"
                    variant="danger"
                    @click="showNewTicketForm = true"
                  >
                    Crear Ticket
                  </b-button>
                </div>

                <b-form
                  v-if="showNewTicketForm"
                  @submit.prevent="submitTicket"
                >
                  <b-form-group label="Asunto" label-for="ticket-subject">
                    <b-form-input
                      id="ticket-subject"
                      v-model.trim="newTicketForm.subject"
                      type="text"
                      required
                      placeholder="Describe brevemente el asunto"
                    />
                  </b-form-group>

                  <b-form-group label="Categoría" label-for="ticket-category">
                    <b-form-select v-model="newTicketForm.category_id" required>
                      <b-form-select-option :value="null">Seleccione una categoría</b-form-select-option>
                      <b-form-select-option :value="cat.id" v-for="(cat, catInx) in categories" :key="'catInx-'+catInx">{{ cat.name }}</b-form-select-option>
                    </b-form-select>
                  </b-form-group>

                  <b-form-group label="Descripción" label-for="ticket-description">
                    <b-form-textarea
                      id="ticket-description"
                      v-model.trim="newTicketForm.description"
                      rows="4"
                      required
                      placeholder="Describe tu problema o consulta en detalle"
                    />
                  </b-form-group>

                  <div class="d-flex">
                    <b-button type="submit" variant="danger" class="flex-grow-1 mr-2">
                      Enviar Ticket
                    </b-button>
                    <b-button
                      type="button"
                      variant="outline-secondary"
                      class="flex-grow-1"
                      @click="closeTicketForm"
                    >
                      Cancelar
                    </b-button>
                  </div>
                </b-form>

                <div v-else class="text-center py-5">
                  <i class="fa-solid fa-comments fa-4x text-muted mb-3 d-block"></i>
                  <p class="text-muted mb-0">
                    Haz clic en "Crear Ticket" para levantar una nueva solicitud de soporte
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Mis Tickets -->
          <div class="col-12 col-lg-6">
            <div class="card border-0 shadow h-100">
              <div class="card-body p-4">
                <div class="d-flex align-items-center mb-4">
                  <div class="bg-dark rounded p-3 mr-3">
                    <i class="fa-solid fa-ticket-alt fa-lg text-white"></i>
                  </div>
                  <h3 class="h4 font-weight-bold text-dark mb-0">Mis Tickets</h3>
                </div>

                <div class="ticket-list" style="max-height: 500px; overflow-y: auto;">
                  <div v-if="tickets.length === 0" class="text-center py-5">
                    <i class="fa-solid fa-ticket-alt fa-4x text-muted mb-3 d-block"></i>
                    <p class="text-muted mb-0">No tienes tickets abiertos</p>
                  </div>

                  <!-- Listado de tickets -->
                  <div
                    v-for="ticket in tickets"
                    :key="ticket.id"
                    class="card border mb-3 ticket-card"
                    @click="openCommentModal(ticket)"
                  >
                    <div class="card-body p-3">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <span class="small font-weight-bold text-muted">TICKET-{{ ticket.id }}</span>
                        <span
                          :class="['badge', 'badge-pill', 'px-2', 'py-1', getStatusBadgeClass(ticket.status)]"
                        >
                          <i :class="['fa-solid', 'mr-1', getStatusIcon(ticket.status)]"></i>
                          {{ ticket.status }}
                        </span>
                      </div>
                      <h5 class="h6 font-weight-bold text-dark mb-2">{{ ticket.subject }}</h5>
                      <p class="small text-muted mb-2">{{ ticket.description }}</p>
                      <div class="d-flex align-items-center small text-muted">
                        <i class="fa-solid fa-clock mr-1"></i>
                        <span class="mr-3">{{ ticket.date }}</span>
                        <span class="badge badge-light">{{ ticket.category.name }}</span>
                      </div>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        block
                        class="mt-2"
                        @click.stop="openCommentModal(ticket)"
                      >
                        Ver Detalles
                      </b-button>
                    </div>
                  </div>
                  <!--  -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Modal Detalle del Ticket -->
    <b-modal
      v-model="showTicketModal"
      size="lg"
      centered
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      scrollable 
      header-class="bg-danger text-white"
      body-class="p-0"
      @hidden="hiddenCommentsModal"
    >
      <template v-slot:modal-header="{ close }">
        <div class="d-flex align-items-center w-100">
          <div class="flex-grow-1">
            <h3 class="h5 font-weight-bold mb-1">
              <i class="fa-solid fa-ticket-alt mr-2"></i>
              {{ selectedTicket ? selectedTicket.subject : '' }}
            </h3>
            <small class="text-white-50">TICKET-{{ selectedTicket ? selectedTicket.id : '' }}</small>
          </div>
          <button type="button" class="close text-white" aria-label="Cerrar" @click="close()">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </template>

      <div v-if="selectedTicket" class="p-4">
        <!-- Estado e Info -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="bg-light rounded p-3">
              <div class="d-flex align-items-center text-muted mb-2">
                <i class="fa-solid fa-tag mr-2"></i>
                <span class="small font-weight-medium">Estado</span>
              </div>
              <span
                :class="['badge', 'badge-pill', 'px-3', 'py-2', getStatusBadgeClass(selectedTicket.status)]"
              >
                <i :class="['fa-solid', 'mr-1', getStatusIcon(selectedTicket.status)]"></i>
                {{ selectedTicket.status }}
              </span>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="bg-light rounded p-3">
              <div class="d-flex align-items-center text-muted mb-2">
                <i class="fa-solid fa-calendar mr-2"></i>
                <span class="small font-weight-medium">Fecha de Creación</span>
              </div>
              <p class="font-weight-bold text-dark mb-0">{{ selectedTicket.date }}</p>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="bg-light rounded p-3">
              <div class="d-flex align-items-center text-muted mb-2">
                <i class="fa-solid fa-file-alt mr-2"></i>
                <span class="small font-weight-medium">Categoría</span>
              </div>
              <p class="font-weight-bold text-dark mb-0">{{ selectedTicket.category.name }}</p>
            </div>
          </div>
        </div>

        <!-- Descripción -->
        <div class="border-top pt-4 mb-4">
          <h4 class="h6 font-weight-bold text-dark mb-3">
            <i class="fa-solid fa-file-alt text-danger mr-2"></i>
            Descripción del Problema
          </h4>
          <p class="text-muted bg-light p-3 rounded">
            {{ selectedTicket.description }}
          </p>
        </div>

        <!-- Historial -->
        <div class="border-top pt-4 mb-4">
          <h4 class="h6 font-weight-bold text-dark mb-3">
            <i class="fa-solid fa-clock text-danger mr-2"></i>
            Historial de Actualizaciones
          </h4>
          <div class="timeline">
            <!-- Comentarios -->
            <div class="d-flex mb-4" v-for="(comment, cInx) in selectedTicket.comments" :key="'cInx-'+cInx+comment.id">
              <div class="flex-shrink-0">
                <div class="rounded-circle support-timeline-icon support-timeline-success d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                  <i class="fa-solid fa-check-circle" :class="{ 'text-primary' : comment.user == 'Equipo de Soporte', 'text-success' : comment.user != 'Equipo de Soporte' }"></i>
                </div>
                <div class="bg-secondary mx-auto mt-1" style="width: 2px; height: 40px;"></div>
              </div>

              <div class="flex-grow-1 ml-3">
                <div class="bg-light p-3 rounded">
                  <div class="d-flex align-items-center mb-2" style="line-height: 1.1;">
                    <i class="fa-solid fa-user text-muted mr-2"></i>
                    <span class="font-weight-bold text-dark">{{ comment.user }}</span>
                    <span class="text-muted small ml-2">• {{ comment.date }}</span>
                  </div>
                  <p class="text-muted small mb-0">
                    {{ comment.comment }}
                  </p>
                </div>
              </div>
            </div>
            <!-- Comentario fin -->

            <!-- <div class="d-flex">
              <div class="flex-shrink-0">
                <div class="rounded-circle support-timeline-icon support-timeline-primary d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">
                  <i class="fa-solid fa-exclamation-circle text-primary"></i>
                </div>
              </div>
              <div class="flex-grow-1 ml-3">
                <div class="bg-light p-3 rounded">
                  <div class="d-flex align-items-center mb-2">
                    <i class="fa-solid fa-user text-muted mr-2"></i>
                    <span class="font-weight-bold text-dark">Tú</span>
                    <span class="text-muted small ml-2">• {{ selectedTicket.date }}</span>
                  </div>
                  <p class="text-muted small mb-0">Ticket creado</p>
                </div>
              </div>
            </div> -->
          </div>
        </div>

        <!-- Botones -->
        <b-form class="border-top pt-4 row" @submit.prevent="newComment">
          <div class="col-12 mb-3" v-if="showNewCommentForm">
            <b-form-textarea
              id="textarea"
              v-model="newCommentForm.comment"
              placeholder="Escriba un comentario..."
              rows="3"
              max-rows="3"
              required
            ></b-form-textarea>
          </div>

          <div class="col-12 d-flex">
            <b-button type="submit" variant="danger" class="flex-grow-1 mr-2" v-if="selectedTicket.status != 'Resuelto'">
              Agregar Comentario
            </b-button>
            <b-button variant="outline-secondary" class="flex-grow-1" @click="showTicketModal = false">
              Cerrar
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'SupportPage',
  data() {
    return {
      isLoading: false,
      showNewTicketForm: false,
      showTicketModal: false,
      showNewCommentForm: false,
      hideTicketForm: true,

      selectedTicket: null,

      newTicketForm: {
        subject: '',
        category_id: null,
        description: '',
      },

      newCommentForm: {
        ticket_id: null,
        comment: null,
      },
      
      categories: [],
      tickets: [
        // {
        //   id: 'TICKET-001',
        //   subject: 'Problema con instalación de equipo',
        //   category: 'Instalación',
        //   status: 'en-proceso',
        //   date: '2024-01-15',
        //   description: 'El equipo no enciende después de la instalación inicial',
        // },
        // {
        //   id: 'TICKET-002',
        //   subject: 'Consulta sobre garantía',
        //   category: 'Garantía',
        //   status: 'resuelto',
        //   date: '2024-01-10',
        //   description: 'Necesito información sobre extensión de garantía',
        // },
      ],
    };
  },

  methods: {
    toast(message, title, variant, autoHideDelay = 5000) {
      this.$bvToast.toast(message, {
        title: title,
        variant: variant,
        solid: true,
        autoHideDelay: autoHideDelay,
      });
    },

    getStatusBadgeClass(status) {
      const map = {
        Abierto: 'badge-primary',
        'En proceso': 'badge-warning',
        Resuelto: 'badge-success',
      };
      return map[status] || 'badge-secondary';
    },

    getStatusIcon(status) {
      const map = {
        Abierto: 'fa-exclamation-circle',
        'En proceso': 'fa-clock',
        Resuelto: 'fa-check-circle',
      };
      return map[status] || 'fa-ticket-alt';
    },

    getTickets() {
      this.isLoading = true;
      axios.post(tools.url('/api/tickets')).then((response) => {
        this.tickets = response.data.tickets;
        this.categories = response.data.categories;
        this.hideTicketForm = false;
        this.isLoading = false;
      }).catch((error) => {
        this.isLoading = false;
        this.toast(error.response.data.message, 'Error', 'danger');

        if(error.response.data.type == 'error-no-purchases') {
          this.hideTicketForm = true;
        }
      });
    },

    // == Formulario de Nuevo Ticket ==
    closeTicketForm() {
      this.showNewTicketForm = false;
      this.newTicketForm = {
        subject: '',
        category_id: null,
        description: '',
      };
    },

    submitTicket() {
      if(this.newTicketForm.description.replace(/\s/g, '').length <= 20) {
        this.toast('La descripción debe tener al menos 20 caracteres', 'Error', 'danger');
      }else {
        this.isLoading = true;
        axios.post(tools.url('/api/tickets/new'), this.newTicketForm).then((response) => {
          this.isLoading = false;
          this.toast('Ticket creado exitosamente. Nos comunicaremos contigo lo antes posible.', 'Ticket creado', 'success', 8000);
          this.showNewTicketForm = false;
          this.newTicketForm = {
            subject: '',
            category_id: null,
            description: '',
          };
          
          this.getTickets();
        }).catch((error) => {
          this.toast(error.response.data.message, 'Error', 'danger');
          this.isLoading = false;
        });
      }
    },
    // == Formulario de Nuevo Ticket fin ==

    // == Comentario Modal ==
    openCommentModal(ticket) {
      this.selectedTicket = ticket;
      this.newCommentForm.ticket_id = ticket.id;
      this.showTicketModal = true;
    },

    hiddenCommentsModal() {
      this.showNewCommentForm = false;
      this.newCommentForm = {
        ticket_id: null,
        comment: null,
      };
      this.selectedTicket = null;
    },

    newComment() {
      // Mostrar el formulario si no estaba visible
      if(!this.showNewCommentForm) {
        this.showNewCommentForm = true;
      }

      if(this.newCommentForm.comment) {
        if(this.newCommentForm.comment.replace(/\s/g, '').length <= 20) {
          this.toast('El comentario debe tener al menos 20 caracteres', 'Error', 'danger');
        }else {
          this.isLoading = true;
          axios.post(tools.url('/api/tickets/comment'), this.newCommentForm).then((response) => {
            this.showTicketModal = false;
            this.showNewCommentForm = false;
            this.newCommentForm = {
              ticket_id: null,
              comment: null,
            };
            this.selectedTicket = null;
            this.toast(response.data.message, 'Comentario enviado', 'success', 12000);
            
            this.getTickets();
          }).catch((error) => {
            this.toast(error.response.data.message, 'Error', 'danger');
            this.isLoading = false;
            console.log(error);
          });
        }
      }
    }
    // == Comentario Modal fin ==
  },

  mounted() {
    if(this.$root.logged){
      this.getTickets();
    } else {
      this.$router.push("/login");
    }
  },
};
</script>